/**
 * OfflineCacheManager Component
 * 
 * Unified UI for managing all offline data for low-signal oilfield areas:
 * - Map tile cache (download map tiles for work areas)
 * - Facility data cache (download well/facility data by basin)
 * - Monitor cache progress
 * - View cache statistics
 * - Clear cached data
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import { offlineMapCache, CacheProgress, CacheMeta } from '../services/offlineMapCache';
import { cacheService } from '../services/cache';
import { getCyberColors } from '../theme/cyberColors';

interface WorkArea {
  id: string;
  name: string;
  center: { lat: number; lng: number };
  bounds: { north: number; south: number; east: number; west: number };
  tileEstimate: number;
  estimatedSizeMB: number;
}

interface CacheStats {
  tileCount: number;
  staticMapCount: number;
  totalSizeMB: number;
  areas: CacheMeta[];
}

// Basin data definitions
const BASINS = [
  { id: 'delaware', name: 'Delaware Basin', counties: 'Eddy + Lea (NM)', state: 'NM' },
  { id: 'permian', name: 'Permian Basin', counties: 'Southeast NM', state: 'NM' },
  { id: 'san-juan', name: 'San Juan Basin', counties: 'Northwest NM', state: 'NM' },
  { id: 'raton', name: 'Raton Basin', counties: 'Northeast NM', state: 'NM' },
  { id: 'tx-permian', name: 'TX Permian Basin', counties: 'Midland, Ector, Andrews, + more', state: 'TX' },
];

interface OfflineCacheManagerProps {
  open: boolean;
  onClose: () => void;
  darkMode: boolean;
}

export function OfflineCacheManager({ open, onClose, darkMode }: OfflineCacheManagerProps) {
  const [areas, setAreas] = useState<WorkArea[]>([]);
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingArea, setDownloadingArea] = useState<string | null>(null);
  const [progress, setProgress] = useState<CacheProgress | null>(null);
  const [cachedAreas, setCachedAreas] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  
  // Facility data state
  const [activeTab, setActiveTab] = useState(0);
  const [basinDownloadLoading, setBasinDownloadLoading] = useState<Record<string, boolean>>({});
  const [downloadedBasins, setDownloadedBasins] = useState<Record<string, boolean>>({});
  const [facilityStats, setFacilityStats] = useState<{ total: number; counties: number } | null>(null);
  const [integrityVerified, setIntegrityVerified] = useState(false);

  const cyber = getCyberColors(darkMode, false);

  // Load available areas and cache stats
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch available map tile areas
      const areasResponse = await fetch('/api/offline/map-cache?action=areas');
      if (areasResponse.ok) {
        const data = await areasResponse.json();
        setAreas(data.areas || []);
      }

      // Get map tile cache stats
      const cacheStats = await offlineMapCache.getCacheStats();
      setStats(cacheStats);

      // Check which map areas are cached
      const cached = new Set<string>();
      for (const meta of cacheStats.areas) {
        cached.add(meta.area);
      }
      setCachedAreas(cached);
      
      // Get facility data cache stats
      const facilitySize = await cacheService.getTotalCacheSize();
      setFacilityStats({ 
        total: facilitySize.total,
        counties: facilitySize.facilities || 0
      });
      
      // Check which basins have cached data
      const basinStatus: Record<string, boolean> = {};
      for (const basin of BASINS) {
        const counties = getBasinCounties(basin.id);
        let hasData = false;
        for (const county of counties) {
          const cached = await cacheService.getCachedData(county, 'wells');
          if (cached && cached.length > 0) {
            hasData = true;
            break;
          }
        }
        basinStatus[basin.id] = hasData;
      }
      setDownloadedBasins(basinStatus);
    } catch (err) {
      console.error('Failed to load offline cache data:', err);
      setError('Failed to load cache data. Make sure the API is running.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Get counties for a basin
  const getBasinCounties = (basinId: string): string[] => {
    switch (basinId) {
      case 'delaware': return ['Eddy', 'Lea'];
      case 'permian': return ['Chaves', 'Roosevelt', 'Curry', 'De Baca', 'Lincoln'];
      case 'san-juan': return ['San Juan', 'Rio Arriba', 'McKinley', 'Sandoval'];
      case 'raton': return ['Colfax'];
      case 'tx-permian': return ['Ector', 'Midland', 'Andrews', 'Martin', 'Howard', 'Ward', 'Winkler', 'Loving', 'Reeves', 'Pecos'];
      default: return [];
    }
  };
  
  // Download facility data for a basin
  const handleDownloadBasin = async (basinId: string) => {
    setBasinDownloadLoading(prev => ({ ...prev, [basinId]: true }));
    setError(null);
    
    try {
      const counties = getBasinCounties(basinId);
      const basin = BASINS.find(b => b.id === basinId);
      const state = basin?.state || 'NM';
      
      for (const county of counties) {
        const url = `/api/facilities/search?type=wells&format=compact&county=${encodeURIComponent(county)}&state=${state}`;
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          if (data.results) {
            await cacheService.cacheFacilityType('wells', county, data.results, state);
          }
        }
      }
      
      setDownloadedBasins(prev => ({ ...prev, [basinId]: true }));
      
      // Refresh stats
      const facilitySize = await cacheService.getTotalCacheSize();
      setFacilityStats({ 
        total: facilitySize.total,
        counties: facilitySize.facilities || 0
      });
    } catch (err) {
      console.error('Basin download failed:', err);
      setError(`Failed to download ${basinId}. Please try again.`);
    } finally {
      setBasinDownloadLoading(prev => ({ ...prev, [basinId]: false }));
    }
  };
  
  // Verify cache integrity
  const handleVerifyIntegrity = async () => {
    setLoading(true);
    try {
      const result = await cacheService.verifyIntegrity();
      setIntegrityVerified(result.corrupted.length === 0);
      if (result.corrupted.length > 0) {
        setError('Cache integrity check failed. Consider re-downloading.');
      }
    } catch (err) {
      setError('Failed to verify cache integrity.');
    } finally {
      setLoading(false);
    }
  };
  
  // Clear facility cache
  const handleClearFacilityCache = async () => {
    try {
      await cacheService.clearCache();
      setDownloadedBasins({});
      setFacilityStats({ total: 0, counties: 0 });
      setIntegrityVerified(false);
    } catch (err) {
      console.error('Failed to clear facility cache:', err);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  // Download an area
  const handleDownload = async (areaId: string) => {
    setDownloadingArea(areaId);
    setProgress({ total: 0, completed: 0, failed: 0, sizeMB: 0, status: 'idle' });
    setError(null);

    try {
      const result = await offlineMapCache.cacheArea(areaId, (p) => {
        setProgress({ ...p });
      });

      if (result.success) {
        setCachedAreas((prev) => new Set([...prev, areaId]));
      } else if (result.failed > 0) {
        setError(`Cached ${result.cached} tiles, but ${result.failed} failed.`);
      }

      // Refresh stats
      const newStats = await offlineMapCache.getCacheStats();
      setStats(newStats);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed. Please try again.');
    } finally {
      setDownloadingArea(null);
      setProgress(null);
    }
  };

  // Clear an area's cache
  const handleClearArea = async (areaId: string) => {
    try {
      await offlineMapCache.clearArea(areaId);
      setCachedAreas((prev) => {
        const next = new Set(prev);
        next.delete(areaId);
        return next;
      });
      const newStats = await offlineMapCache.getCacheStats();
      setStats(newStats);
    } catch (err) {
      console.error('Failed to clear area:', err);
    }
  };

  // Clear all cache
  const handleClearAll = async () => {
    try {
      await offlineMapCache.clearAll();
      setCachedAreas(new Set());
      const newStats = await offlineMapCache.getCacheStats();
      setStats(newStats);
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.98))'
            : 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: cyber.textPrimary,
        borderBottom: `1px solid ${cyber.borderDefault}`,
        pb: 1,
      }}>
        <CloudOffIcon sx={{ color: cyber.neonCyan }} />
        Offline Cache
        <Chip
          size="small"
          icon={<SignalWifiOffIcon />}
          label="For Low Signal Areas"
          sx={{ ml: 'auto', bgcolor: cyber.neonBlue + '20', color: cyber.neonBlue }}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs for Map Tiles vs Facility Data */}
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: `1px solid ${cyber.borderDefault}`,
            '& .MuiTab-root': { 
              color: cyber.textMuted,
              '&.Mui-selected': { color: cyber.neonCyan }
            },
            '& .MuiTabs-indicator': { bgcolor: cyber.neonCyan },
          }}
        >
          <Tab icon={<MapIcon />} label="Map Tiles" iconPosition="start" />
          <Tab icon={<LocalGasStationIcon />} label="Facility Data" iconPosition="start" />
        </Tabs>
        
        <Box sx={{ p: 2 }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Loading State */}
          {loading && !downloadingArea && Object.values(basinDownloadLoading).every(v => !v) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: cyber.neonCyan }} />
            </Box>
          )}
          
          {/* ===== TAB 0: MAP TILES ===== */}
          {activeTab === 0 && !loading && (
            <>
              {/* Cache Statistics */}
              {stats && (
                <Card sx={{ mb: 2, bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)' }}>
                  <CardContent sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <StorageIcon sx={{ color: cyber.neonCyan, fontSize: 32 }} />
                      <Typography variant="h5" sx={{ color: cyber.textPrimary }}>
                        {stats.totalSizeMB} MB
                      </Typography>
                      <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                        Total Cached
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <MapIcon sx={{ color: cyber.neonGreen, fontSize: 32 }} />
                      <Typography variant="h5" sx={{ color: cyber.textPrimary }}>
                        {stats.tileCount.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                        Map Tiles
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon sx={{ color: cyber.neonPurple, fontSize: 32 }} />
                      <Typography variant="h5" sx={{ color: cyber.textPrimary }}>
                        {stats.areas.length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                        Areas Cached
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                      <Tooltip title="Refresh">
                        <IconButton onClick={loadData} disabled={loading}>
                          <RefreshIcon sx={{ color: cyber.textMuted }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear All Map Cache">
                        <IconButton onClick={handleClearAll} disabled={loading || stats.tileCount === 0}>
                          <DeleteIcon sx={{ color: cyber.neonRed }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Download Progress */}
              {progress && downloadingArea && (
                <Card sx={{ mb: 2, bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ color: cyber.textPrimary, mb: 1 }}>
                      Downloading: {areas.find(a => a.id === downloadingArea)?.name}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(progress.completed / Math.max(progress.total, 1)) * 100}
                      sx={{
                        mb: 1,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: cyber.bgCard,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: cyber.neonCyan,
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                      {progress.completed.toLocaleString()} / {progress.total.toLocaleString()} tiles
                      ({progress.sizeMB.toFixed(1)} MB)
                      {progress.failed > 0 && ` • ${progress.failed} failed`}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Areas List */}
              <Typography variant="subtitle1" sx={{ color: cyber.textPrimary, mb: 1, fontWeight: 600 }}>
                Permian Basin Work Areas
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {areas.map((area) => {
                  const isCached = cachedAreas.has(area.id);
                  const isDownloading = downloadingArea === area.id;
                  const cachedMeta = stats?.areas.find(m => m.area === area.id);

                  return (
                    <ListItem
                      key={area.id}
                      sx={{
                        bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
                        borderRadius: 2,
                        mb: 1,
                        border: isCached 
                          ? `1px solid ${cyber.neonGreen}40`
                          : `1px solid ${cyber.borderDefault}`,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {area.name}
                            {isCached && (
                              <CheckCircleIcon sx={{ color: cyber.neonGreen, fontSize: 18 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                            {isCached && cachedMeta
                              ? `Cached: ${cachedMeta.tileCount.toLocaleString()} tiles (${cachedMeta.sizeMB} MB)`
                              : `~${area.tileEstimate.toLocaleString()} tiles • ~${area.estimatedSizeMB} MB`
                            }
                          </Typography>
                        }
                        primaryTypographyProps={{ sx: { color: cyber.textPrimary } }}
                      />
                      <ListItemSecondaryAction>
                        {isCached ? (
                          <Tooltip title="Clear Cache">
                            <IconButton
                              onClick={() => handleClearArea(area.id)}
                              disabled={isDownloading}
                              size="small"
                            >
                              <DeleteIcon sx={{ color: cyber.neonRed }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={isDownloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                            onClick={() => handleDownload(area.id)}
                            disabled={!!downloadingArea}
                            sx={{
                              borderColor: cyber.neonCyan,
                              color: cyber.neonCyan,
                              '&:hover': {
                                borderColor: cyber.neonCyan,
                                bgcolor: cyber.neonCyan + '20',
                              },
                            }}
                          >
                            {isDownloading ? 'Downloading...' : 'Download'}
                          </Button>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>

              {/* Help Text */}
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  bgcolor: darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                  '& .MuiAlert-icon': { color: cyber.neonBlue },
                }}
              >
                <Typography variant="body2">
                  <strong>Map Tiles:</strong> Download map tiles for areas where you frequently work. 
                  Once cached, maps will load even without cell signal.
                </Typography>
              </Alert>
            </>
          )}
          
          {/* ===== TAB 1: FACILITY DATA ===== */}
          {activeTab === 1 && !loading && (
            <>
              {/* Facility Cache Statistics */}
              <Card sx={{ mb: 2, bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)' }}>
                <CardContent sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <StorageIcon sx={{ color: cyber.neonOrange, fontSize: 32 }} />
                    <Typography variant="h5" sx={{ color: cyber.textPrimary }}>
                      {facilityStats ? `${(facilityStats.total / 1024).toFixed(1)} KB` : '0 KB'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                      Facility Data
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <LocalGasStationIcon sx={{ color: cyber.neonGreen, fontSize: 32 }} />
                    <Typography variant="h5" sx={{ color: cyber.textPrimary }}>
                      {Object.values(downloadedBasins).filter(Boolean).length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                      Basins Cached
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    {integrityVerified ? (
                      <VerifiedIcon sx={{ color: cyber.neonGreen, fontSize: 32 }} />
                    ) : (
                      <VerifiedIcon sx={{ color: cyber.textMuted, fontSize: 32 }} />
                    )}
                    <Typography variant="h5" sx={{ color: integrityVerified ? cyber.neonGreen : cyber.textMuted }}>
                      {integrityVerified ? '✓' : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                      Verified
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    <Tooltip title="Verify Integrity">
                      <IconButton onClick={handleVerifyIntegrity} disabled={loading}>
                        <VerifiedIcon sx={{ color: cyber.textMuted }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear All Facility Cache">
                      <IconButton onClick={handleClearFacilityCache} disabled={loading}>
                        <DeleteIcon sx={{ color: cyber.neonRed }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>

              {/* Basin Download List */}
              <Typography variant="subtitle1" sx={{ color: cyber.textPrimary, mb: 1, fontWeight: 600 }}>
                Download Facility Data by Basin
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {BASINS.map((basin) => {
                  const isCached = downloadedBasins[basin.id];
                  const isDownloading = basinDownloadLoading[basin.id];

                  return (
                    <ListItem
                      key={basin.id}
                      sx={{
                        bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
                        borderRadius: 2,
                        mb: 1,
                        border: isCached 
                          ? `1px solid ${cyber.neonGreen}40`
                          : `1px solid ${cyber.borderDefault}`,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {basin.name}
                            {isCached && (
                              <CheckCircleIcon sx={{ color: cyber.neonGreen, fontSize: 18 }} />
                            )}
                            <Chip 
                              label={basin.state} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem',
                                bgcolor: basin.state === 'TX' ? cyber.neonOrange + '30' : cyber.neonBlue + '30',
                                color: basin.state === 'TX' ? cyber.neonOrange : cyber.neonBlue,
                              }} 
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                            {isCached ? `Downloaded • ${basin.counties}` : basin.counties}
                          </Typography>
                        }
                        primaryTypographyProps={{ sx: { color: isCached ? cyber.neonGreen : cyber.textPrimary } }}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownloadBasin(basin.id)}
                          disabled={Object.values(basinDownloadLoading).some(Boolean)}
                          sx={{
                            borderColor: isCached ? cyber.neonGreen : cyber.neonOrange,
                            color: isCached ? cyber.neonGreen : cyber.neonOrange,
                            '&:hover': {
                              borderColor: isCached ? cyber.neonGreen : cyber.neonOrange,
                              bgcolor: (isCached ? cyber.neonGreen : cyber.neonOrange) + '20',
                            },
                          }}
                        >
                          {isDownloading ? 'Downloading...' : isCached ? 'Update' : 'Download'}
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>

              {/* Help Text */}
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  bgcolor: darkMode ? 'rgba(249, 115, 22, 0.1)' : 'rgba(249, 115, 22, 0.05)',
                  '& .MuiAlert-icon': { color: cyber.neonOrange },
                }}
              >
                <Typography variant="body2">
                  <strong>Facility Data:</strong> Download well and facility information for offline search.
                  Search works without internet once data is cached.
                </Typography>
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${cyber.borderDefault}`, p: 2 }}>
        <Button onClick={onClose} sx={{ color: cyber.textMuted }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OfflineCacheManager;
